import { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CodeEditor from "../components/CodeEditor";
import AssignmentTimer from "./AssignmentTimer";
import { getToken, logout, getUserId } from "../utils/auth";
import swal from "sweetalert";
import "./AssignmentSolveQuestionPage.css";

export default function AssignmentSolveQuestionPage() {
  const { assignmentId, questionId } = useParams();
  const navigate = useNavigate();
  const studentId = getUserId(); 

  const [question, setQuestion] = useState(null);
  
  // Default language state to "c"
  const [language, setLanguage] = useState("c");
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(
    () => typeof document !== "undefined" && !!document.fullscreenElement
  );

  const autoSubmittedRef = useRef(false);

  // eslint-disable-next-line
  const [showLanguageConfirm, setShowLanguageConfirm] = useState(false);
  // eslint-disable-next-line
  const [highlightActive, setHighlightActive] = useState(false);
  const languageSelectRef = useRef(null);

  const requestFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
      } catch {
        // Request may be blocked without user interaction
      }
    }
  };

  // ✅ Updated normalization to focus on C, C++, and Java keys
  const normalizeLanguageKey = useCallback((lang) => {
    if (!lang) return lang;
    const lowerLang = lang.toLowerCase().trim();
    if (lowerLang === "c++" || lowerLang === "cpp") return "cpp";
    if (lowerLang === "java") return "java";
    if (lowerLang === "c") return "c";
    return lowerLang;
  }, []);

  const getBackendLanguage = useCallback((lang) => {
    if (!lang) return lang;
    return normalizeLanguageKey(lang);
  }, [normalizeLanguageKey]);

  const getPredefinedCodeForLanguage = useCallback(
    (question, lang) => {
      if (!question?.predefinedCode) return "";

      const normalizedLang = normalizeLanguageKey(lang);
      const found = question.predefinedCode.find(
        (p) => normalizeLanguageKey(p.language) === normalizedLang
      );
      return found ? found.code : "";
    },
    [normalizeLanguageKey]
  );

  // ✅ Removed Python target function extraction logic (All remaining options use curly brace scoping)
  const extractTargetFunction = useCallback(
    (fullCode, functionName) => {
      if (!fullCode || !functionName) return fullCode;

      const strictRegex = new RegExp(`(?:bold|long|short|unsigned|signed|int|void|double|float|char|string|bool|auto|std\\.\\w+|public|private|protected|static)\\s+${functionName}\\s*\\([^)]*\\)\\s*\\{`);
      let match = fullCode.match(strictRegex);
      if (!match) {
        const fallbackRegex = new RegExp(`(?:\\w+\\s+)+${functionName}\\s*\\([^)]*\\)\\s*\\{`);
        match = fullCode.match(fallbackRegex);
      }
      if (!match) return fullCode;

      let startIndex = match.index;
      let braceCount = 0;
      let endIndex = -1;
      let started = false;

      for (let i = startIndex; i < fullCode.length; i++) {
        if (fullCode[i] === "{") {
          braceCount++;
          started = true;
        } else if (fullCode[i] === "}") {
          braceCount--;
        }
        if (started && braceCount === 0) {
          endIndex = i;
          break;
        }
      }

      if (endIndex !== -1) {
        return fullCode.substring(startIndex, endIndex + 1).trim();
      }

      return fullCode;
    },
    []
  );

  // ✅ Removed Python template reconstruction logic
  const reconstructFullCode = useCallback(
    (userFuncCode, question, lang) => {
      const originalTemplate = getPredefinedCodeForLanguage(question, lang);
      const functionName = question.targetFunctionName;
      if (!originalTemplate || !functionName) return userFuncCode;

      const regex = new RegExp(`(?:\\w+\\s+)+${functionName}\\s*\\([^)]*\\)\\s*\\{`);
      const match = originalTemplate.match(regex);
      if (!match) return userFuncCode;

      const startIndex = match.index;
      let braceCount = 0;
      let endIndex = -1;
      let started = false;

      for (let i = startIndex; i < originalTemplate.length; i++) {
        if (originalTemplate[i] === "{") {
          braceCount++;
          started = true;
        } else if (originalTemplate[i] === "}") {
          braceCount--;
        }
        if (started && braceCount === 0) {
          endIndex = i;
          break;
        }
      }

      if (endIndex !== -1) {
        const before = originalTemplate.substring(0, startIndex);
        const after = originalTemplate.substring(endIndex + 1);
        return `${before}${userFuncCode}${after}`;
      }

      return userFuncCode;
    },
    [getPredefinedCodeForLanguage]
  );

  const completedKey = `assignment_completed_questions_${assignmentId}_${studentId}`;
  const submissionKey = `assignment_submission_${assignmentId}_${studentId}`;

  const readSubmissionObject = useCallback(() => {
    try {
      const raw = localStorage.getItem(submissionKey);
      if (!raw) {
        return {
          assignmentId,
          studentId,
          solvedQuestions: [],
          isFinalSubmission: false,
        };
      }
      return JSON.parse(raw);
    } catch (e) {
      return {
        assignmentId,
        studentId,
        solvedQuestions: [],
        isFinalSubmission: false,
      };
    }
  }, [submissionKey, assignmentId, studentId]);

  const saveSubmissionObject = useCallback((obj) => {
    try {
      localStorage.setItem(submissionKey, JSON.stringify(obj));
    } catch (e) {}
  }, [submissionKey]);

  const upsertSolvedQuestionInSubmission = useCallback((qId, qCode) => {
    try {
      const submission = readSubmissionObject();
      const idx = submission.solvedQuestions.findIndex((s) => s.questionId === qId);
      if (idx !== -1) {
        submission.solvedQuestions[idx].code = qCode || "";
      } else {
        submission.solvedQuestions.push({ questionId: qId, code: qCode || "" });
      }
      saveSubmissionObject(submission);
    } catch (e) {}
  }, [readSubmissionObject, saveSubmissionObject]);

  const codeStorageKey = (studentIdVal, assignmentIdVal, questionIdVal, langVal) =>
    `student_${studentIdVal}_assignment_${assignmentIdVal}_question_${questionIdVal}_lang_${langVal}`;

  const saveCurrentCode = useCallback(() => {
    try {
      if (!studentId || !assignmentId || !questionId) return;
      const key = codeStorageKey(studentId, assignmentId, questionId, language);
      localStorage.setItem(key, code || "");
      try {
        upsertSolvedQuestionInSubmission(questionId, code || "");
      } catch (e) {}
    } catch (e) {}
  }, [studentId, assignmentId, questionId, language, code, upsertSolvedQuestionInSubmission]);

  const scrollToResult = useCallback(() => {
    setTimeout(() => {
      const resultElement = document.querySelector('.result-container');
      if (resultElement) {
        resultElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  const markQuestionCompleted = useCallback(() => {
    const completed = JSON.parse(localStorage.getItem(completedKey)) || [];

    if (!completed.includes(questionId)) {
      completed.push(questionId);
      localStorage.setItem(completedKey, JSON.stringify(completed));
      try {
        upsertSolvedQuestionInSubmission(questionId, code || "");
      } catch (e) {}
    }
  }, [completedKey, questionId, code, upsertSolvedQuestionInSubmission]);

  useEffect(() => {
    const forceKey = `assignment_force_submit_${assignmentId}`;

    const triggerViolation = () => {
      if (autoSubmittedRef.current) return;
      autoSubmittedRef.current = true;
      localStorage.setItem(forceKey, "true");
      navigate(`/assignments/solve/${assignmentId}`);
    };

    const handleFullscreenChange = () => {
      const inFullscreen = !!document.fullscreenElement;
      setIsFullscreen(inFullscreen);
      if (!inFullscreen) triggerViolation();
    };

    const handleBlur = () => {
      triggerViolation();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) triggerViolation();
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [assignmentId, navigate]);

  useEffect(() => {
    async function fetchQuestion() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/questions/${questionId}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`
            }
          }
        );

        if (res.status === 401 || res.status === 403) {
          logout();
          navigate("/login");
          return;
        }

        const data = await res.json();
        setQuestion(data);
      } catch {
        console.error("Failed to load question");
      }
    }

    fetchQuestion();
  }, [questionId, navigate]);

  useEffect(() => {
    if (!question) return;

    try {
      const key = codeStorageKey(studentId, assignmentId, questionId, language);
      const saved = localStorage.getItem(key);
      if (saved) {
        setCode(saved);
        return;
      }
    } catch (e) {}

    const fullStarterCode = getPredefinedCodeForLanguage(question, language);
    if (question.isTargetFunction && question.targetFunctionName) {
      const isolatedFunc = extractTargetFunction(
        fullStarterCode,
        question.targetFunctionName
      );
      setCode(isolatedFunc || "");
    } else {
      setCode(fullStarterCode || "");
    }
  }, [question, language, extractTargetFunction, getPredefinedCodeForLanguage, studentId, assignmentId, questionId]);

  // Debounced autosave whenever code changes
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (!studentId || !assignmentId || !questionId) return;
    try {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        const key = codeStorageKey(studentId, assignmentId, questionId, language);
        try {
          localStorage.setItem(key, code || "");
        } catch (e) {}
      }, 900);
    } catch (e) {}

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [code, studentId, assignmentId, questionId, language]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        if (!studentId || !assignmentId || !questionId) return;
        const key = codeStorageKey(studentId, assignmentId, questionId, language);
        localStorage.setItem(key, code || "");
      } catch (e) {}
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [studentId, assignmentId, questionId, code, language]);

  useEffect(() => {
    return () => {
      try {
        saveCurrentCode();
      } catch (e) {}
    };
  }, [saveCurrentCode]);

  const submitAssignment = useCallback(async () => {
    setLoading(true);
    setResult(null);

    try {
      upsertSolvedQuestionInSubmission(questionId, code || "");
    } catch (e) {}

    try {
      const finalExecutableCode = question?.isTargetFunction
        ? reconstructFullCode(code, question, language)
        : code;

      const res = await fetch(
        'https://backend.teamtechsign.in/run',
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            language: getBackendLanguage(language),
            code: finalExecutableCode,
            questionId,
            assignmentId,
            studentId
          })
        }
      );

      const data = await res.json();
      const summaryOnly = { ...data };
      delete summaryOnly.results;
    
      setResult(summaryOnly);
      scrollToResult();

      if (data.passed && data.total && data.passed === data.total) {
        markQuestionCompleted();
        swal({
          title: "Success!",
          text: "Question solved successfully!",
          icon: "success",
          buttons: false,
          timer: 3000
        });
      }
    } catch {
      setResult({ error: "Submission failed" });
    } finally {
      setLoading(false);
    }
  }, [
    language,
    code,
    question,
    questionId,
    assignmentId,
    studentId,
    markQuestionCompleted,
    reconstructFullCode,
    scrollToResult,
    getBackendLanguage,
    upsertSolvedQuestionInSubmission
  ]);

  const submitAssignmentRef = useRef(submitAssignment);
  useEffect(() => {
    submitAssignmentRef.current = submitAssignment;
  }, [submitAssignment]);

  useEffect(() => {
    if (!question) return;
    if (!code.trim()) return;
    if (autoSubmittedRef.current) return;

    autoSubmittedRef.current = true;
    submitAssignmentRef.current && submitAssignmentRef.current();
  }, [question, code]);

  const runSample = async () => {
    setLoading(true);
    setResult(null);

    try {
      const finalExecutableCode = question?.isTargetFunction
        ? reconstructFullCode(code, question, language)
        : code;

      const res = await fetch(
        'https://backend.teamtechsign.in/run',
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            language: getBackendLanguage(language),
            code: finalExecutableCode,
            testcases: question.sampleTestcases
          })
        }
      );

      setResult(await res.json());
      scrollToResult();
    } catch {
      setResult({ error: "Execution failed" });
      scrollToResult();
    } finally {
      setLoading(false);
    }
  };

  if (!question) {
    return <div className="assignment-loader">Loading question...</div>;
  }

  const failedCount = result && !result.results ? result.total - result.passed : 0;

  return (
    <div className="assignment-root">
      {!isFullscreen && (
        <div className="fullscreen-overlay">
          <button className="fullscreen-btn" onClick={requestFullscreen}>
            Enter Full Screen to Continue
          </button>
        </div>
      )}
      <div className="assignment-layout">
        <div className="assignment-left">
          <div className="question-card">
            <div className="question-header">
              <h1>{question.title}</h1>
              <span className={`difficulty ${question.difficulty?.toLowerCase()}`}>
                {question.difficulty}
              </span>
            </div>

            <div
              className="question-description"
              dangerouslySetInnerHTML={{ __html: question.description }}
            />

            <h3 className="section-title">Sample Testcases</h3>
            {question.sampleTestcases.map((tc, i) => (
              <div key={i} className="sample-box">
                <strong>Input</strong>
                <pre>{tc.input}</pre>
                <strong>Output</strong>
                <pre>{tc.output}</pre>
              </div>
            ))}
          </div>
        </div>

        <div className="assignment-right">
          <div className="editor-header">
            <button
              className="btn ghos123"
              onClick={() => {
                saveCurrentCode();
                navigate(-1);
              }}
            >
              ← Go Back
            </button>

            <AssignmentTimer
              assignmentId={assignmentId}
              onTimeUp={() => navigate(`/assignments/solve/${assignmentId}`)}
            />

            {/* ✅ Updated dropdown fields to include C, C++, and Java options only */}
            <select
              ref={languageSelectRef}
              className={highlightActive ? "language-highlight" : ""}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={false}
            >
              <option value="c">C</option>
              <option value="cpp">C++</option>
              <option value="java">Java</option>
            </select>
          </div>

          <div className="editor-wrapper">
            <CodeEditor language={language} code={code} setCode={setCode} />
          </div>

          <div className="editor-actions">
            <button className="btn ghost" onClick={runSample} disabled={loading}>
              ▶ Run
            </button>
            <button className="btn primary" onClick={submitAssignment} disabled={loading}>
              Submit
            </button>
          </div>
        </div>
      </div>

      {result && (
        <div className="result-container">
          {result.results ? (
            <>
              <h3 className="result-title">Run Results</h3>
              <table className="result-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Input</th>
                    <th>Expected</th>
                    <th>Your Output</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((tc, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td><pre>{tc.input}</pre></td>
                      <td><pre>{tc.expected}</pre></td>
                      <td><pre>{tc.actual}</pre></td>
                      <td>
                        <span className={`status ${tc.status === "PASS" ? "ok" : "fail"}`}>
                          {tc.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="submit-summary-text">
              <span className="passed">
                ✅ Passed: <strong>{result.passed}/{result.total}</strong>
              </span>
              <span className="failed">
                ❌ Failed: <strong>{failedCount}/{result.total}</strong>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}